/**
 * The pre-auth language switcher, and the text-fill pass behind it.
 *
 * WHY THIS EXISTS SEPARATELY FROM THE OATH BOOK SWITCHER
 * A visitor meets the marketing page before they have an account, so the
 * in-game Settings switcher is unreachable to them. Browser auto-detection
 * (`detectLocale`) covers the common case, but it is a guess: anyone on a
 * borrowed or mis-configured machine had no way to correct it. This is that way.
 *
 * WHY A FILL PASS AND NOT A RE-RENDER
 * The landing is one big HTML template driven by `landing-fx.js`, which owns
 * parallax, snow, ember canvases and IntersectionObservers. Re-rendering the
 * whole page on switch would reset scroll position and restart every animation —
 * the language would change and the visitor would lose their place. So the copy
 * carries `data-i18n` keys and gets refilled in place; the DOM, and therefore all
 * the motion state, survives untouched.
 *
 * Markup contract, set in `landing-screen.ts`:
 *   data-i18n="key"        → element's textContent
 *   data-i18n-aria="key"   → element's aria-label
 *   data-i18n-n="500"      → named arg `n` for the key (rank point totals)
 */
import { t, setLocale, activeLocale, LOCALES } from '../i18n';
import { hasLocaleChoice, type LocaleId } from '../i18n/locales';

/**
 * Fill every annotated node from the catalogue. Idempotent — safe to re-run on
 * every switch, which is exactly how the switcher repaints.
 */
export function applyLandingI18n(root: HTMLElement): void {
  root.querySelectorAll<HTMLElement>('[data-i18n]').forEach((el) => {
    const key = el.dataset.i18n!;
    const n = el.dataset.i18nN;
    const text = n !== undefined ? t(key, { n }) : t(key);
    // A missing key resolves to the key itself; leaving the shipped English in
    // place beats printing "landing.hero.sub" across the page.
    if (text !== key) el.textContent = text;
  });

  root.querySelectorAll<HTMLElement>('[data-i18n-aria]').forEach((el) => {
    const key = el.dataset.i18nAria!;
    const text = t(key);
    if (text !== key) el.setAttribute('aria-label', text);
  });
}

/**
 * Build the switcher into every mount point (the header keeps one, the mobile
 * drawer another). Returns a teardown.
 *
 * A native <select> on purpose: it is the one control that already works with a
 * keyboard, a screen reader and a thumb on a phone, and the OS renders the
 * options — which matters when the list is in six different scripts.
 */
export function mountLangPick(root: HTMLElement, onSwitched: () => void): () => void {
  // Nothing to choose on a single-language build — render no control at all.
  if (!hasLocaleChoice) return () => {};

  const mounts = [...root.querySelectorAll<HTMLElement>('.lang-pick')];
  const selects: HTMLSelectElement[] = [];

  for (const mount of mounts) {
    mount.innerHTML = '';

    // No decorative rune here. The landing's fonts (Cinzel / Cormorant SC /
    // Alegreya) are subsetted woff2 and carry no Elder Futhark, so a rune falls
    // back to a system face — at 15px in the beam that reads as a rendering
    // glitch, not an ornament. The endonyms are self-explanatory anyway.
    const sel = document.createElement('select');
    sel.className = 'lang-pick-sel';
    sel.setAttribute('aria-label', t('switcher.chooseLanguage'));
    for (const l of LOCALES) {
      const opt = document.createElement('option');
      opt.value = l.id;
      // Each language named in itself — the only labelling a visitor stranded in
      // the wrong tongue can actually read.
      opt.textContent = l.endonym;
      opt.lang = l.tag;
      if (l.id === activeLocale()) opt.selected = true;
      sel.appendChild(opt);
    }

    sel.addEventListener('change', () => {
      const id = sel.value as LocaleId;
      void setLocale(id).then(() => {
        // Keep the other mount point (header ↔ drawer) in step.
        selects.forEach((s) => { s.value = id; });
        selects.forEach((s) => s.setAttribute('aria-label', t('switcher.chooseLanguage')));
        onSwitched();
      });
    });

    mount.append(sel);
    selects.push(sel);
  }

  return () => selects.forEach((s) => s.remove());
}
